import { forwardRef, ButtonHTMLAttributes, useId } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styles from './Button.module.scss';
import { PlacesType } from 'react-tooltip';
import { HOVER_TOOLTIP_ID } from 'src/constants';
import { HoverTooltip } from '../HoverTooltip';

type ButtonSize = 'small' | 'medium';
type ButtonShape = 'regular' | 'round';
type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'secondary-outline'
  | 'transparent'
  | 'link'
  | 'tertiary'
  | 'tertiary-outline'
  | 'highlight'
  | 'error';

export type ButtonCommonProps = {
  size?: ButtonSize;
  shape?: ButtonShape;
  color?: ButtonColor;
  className?: string;
  fullWidth?: boolean;
  fullWidthWithLeftAlign?: boolean;
  tooltipContent?: string;
  tooltipId?: string;
  tooltipPlace?: PlacesType;
};

export type SimpleButtonProps = {
  as?: 'button';
  href?: never;
  to?: never;
  type?: 'button' | 'submit' | 'reset';
} & ButtonCommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

export type LinkProps = {
  as: 'link';
  to: string;
} & ButtonCommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'type'>;

export type AnchorProps = {
  as: 'a';
  href: string;
  to?: never;
} & ButtonCommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'type'>;

export type ButtonProps = SimpleButtonProps | LinkProps | AnchorProps;

type ButtonRefType<T extends ButtonProps> = T extends SimpleButtonProps
  ? HTMLButtonElement
  : HTMLAnchorElement;

export const Button = forwardRef<ButtonRefType<ButtonProps>, ButtonProps>(
  (
    {
      as = 'button',
      href,
      to,
      size = 'medium',
      shape = 'regular',
      color = 'primary',
      className,
      fullWidth = false,
      fullWidthWithLeftAlign = false,
      tooltipContent,
      tooltipId,
      tooltipPlace,
      children,
      ...props
    },
    ref,
  ) => {
    const id = useId();
    const preparedTooltipId = tooltipId || `${HOVER_TOOLTIP_ID}-${id}`;
    const colorWithBorder =
      color === 'secondary-outline' || color === 'tertiary-outline';

    const commonClassNames = classNames(
      styles.root,
      {
        [styles.primary]: color === 'primary',
        [styles.secondary]: color === 'secondary',
        [styles.secondaryOutline]: color === 'secondary-outline',
        [styles.transparent]: color === 'transparent',
        [styles.link]: color === 'link',
        [styles.tertiary]: color === 'tertiary',
        [styles.tertiaryOutline]: color === 'tertiary-outline',
        [styles.highlight]: color === 'highlight',
        [styles.error]: color === 'error',
        [styles.fullWidth]: fullWidth,
        [styles.fullWidthWithLeftAlign]: fullWidthWithLeftAlign,
        // next classes are combinations of main properties. To make all buttons with the same sizes and available to rewrite in a simple way
        [styles.roundSmall]: shape === 'round' && size === 'small',
        [styles.regularSmall]: shape === 'regular' && size === 'small',
        [styles.roundMedium]: shape === 'round' && size === 'medium',
        [styles.regularMedium]: shape === 'regular' && size === 'medium',
        [styles.roundSmallWithBorder]:
          colorWithBorder && shape === 'round' && size === 'small',
        [styles.regularSmallWithBorder]:
          colorWithBorder && shape === 'regular' && size === 'small',
        [styles.roundMediumWithBorder]:
          colorWithBorder && shape === 'round' && size === 'medium',
        [styles.regularMediumWithBorder]:
          colorWithBorder && shape === 'regular' && size === 'medium',
      },
      className,
    );

    const dataAttributes = {
      'data-tooltip-id': tooltipContent ? preparedTooltipId : undefined,
      'data-tooltip-content': tooltipContent,
      'data-tooltip-place': tooltipPlace ? tooltipPlace : undefined,
    };

    const buttonProps = props as ButtonHTMLAttributes<HTMLButtonElement>;

    const linkProps = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;

    const anchorProps = props as React.AnchorHTMLAttributes<HTMLAnchorElement>;

    return (
      <>
        {tooltipContent && <HoverTooltip id={preparedTooltipId} />}

        {as === 'button' && (
          <button
            ref={ref as React.Ref<HTMLButtonElement>}
            className={commonClassNames}
            type={buttonProps.type ?? 'button'}
            {...dataAttributes}
            {...buttonProps}
          >
            {children}
          </button>
        )}

        {as === 'link' && to && (
          <Link
            ref={ref as React.Ref<HTMLAnchorElement>}
            to={to}
            className={commonClassNames}
            {...dataAttributes}
            {...linkProps}
          >
            {children}
          </Link>
        )}

        {as === 'a' && href && (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={href}
            className={commonClassNames}
            {...dataAttributes}
            {...anchorProps}
          >
            {children}
          </a>
        )}
      </>
    );
  },
);
